import { GetData } from "./Functions/SettingData";
import AppCache from "../Helper/Common/AppCache";

const sohListParamsSerializer = (params) => {
	const qs = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((v) => qs.append(key, v));
		} else if (value !== null && value !== undefined) {
			qs.append(key, value);
		}
	});
	return qs.toString();
};

const buildSohListHeaders = (globalFilters) => {
	const headers = {
		"Content-Type": "application/json",
		"excludeNotDefinedOriginDepot": btoa(true),
	};
	if (globalFilters.fleets?.length) {
		headers["fleetIDs"] = btoa(JSON.stringify(globalFilters.fleets));
	}
	if (globalFilters.configuration?.length > 0) {
		headers["configurations"] = btoa(JSON.stringify(globalFilters.configuration));
	}
	if (globalFilters.operationalStatus?.length > 0) {
		headers["operationalStatus"] = btoa(JSON.stringify(globalFilters.operationalStatus));
	}
	if (globalFilters.hierarchicalLocation?.length > 0) {
		headers["originGroupedLocations"] = btoa(JSON.stringify(globalFilters.hierarchicalLocation));
	}
	const preAppliedGlobalFilters = AppCache.getItem("preAppliedGlobalFilters") || {};
	const moduleFilters = preAppliedGlobalFilters['batteryIntelligence'] || {};
	if (moduleFilters.excludeOperationalStatus?.length > 0) {
		headers["excludeOperationalStatus"] = btoa(JSON.stringify(moduleFilters.excludeOperationalStatus));
	}
	return headers;
};

async function getSOHDistribution(globalFilters = {}, additionalFilters = []) {
	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params: {
			select: ["DistinctVehicleCount"],
			groupBy: ["StateOfHealthBucket"],
			filters: JSON.stringify(additionalFilters),
			skipPagination: true,
		},
		paramsSerializer: sohListParamsSerializer,
	};

	return GetData(config);
}

async function getSOHBreakdownChartData(globalFilters = {}, filterName = "", filterValues = []) {
	const filters = filterValues.length > 0
		? [{ name: filterName, value: filterValues }]
		: [];

	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params: {
			select: ["DistinctVehicleCount"],
			groupBy: ["StateOfHealthBucket"],
			filters: JSON.stringify(filters),
			skipPagination: true,
		},
		paramsSerializer: sohListParamsSerializer,
	};
	return GetData(config);
}

async function getRULBreakdownChartData(globalFilters = {}, filterName = "", filterValues = [], rulBucketKey = "RULMonthsBucket") {
	const filters = filterValues.length > 0
		? [{ name: filterName, value: filterValues }]
		: [];

	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params: {
			select: ["DistinctVehicleCount"],
			groupBy: [rulBucketKey],
			filters: JSON.stringify(filters),
			skipPagination: true,
		},
		paramsSerializer: sohListParamsSerializer,
	};
	return GetData(config);
}

async function getSOHBreakdownGroupByData(globalFilters = {}, groupByKey = "originCity") {
	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params: {
			select: ["DistinctVehicleCount"],
			groupBy: [groupByKey],
			filters: JSON.stringify([]),
			skipPagination: true,
		},
		paramsSerializer: sohListParamsSerializer,
	};
	return GetData(config);
}

async function getSOHScatterData(globalFilters = {}, isVehicle = false, additionalFilters = [], extraSelect = []) {
	const select = ["sohEst", "eqCycleEst", "calendarAgeDaysEst"];
	if (isVehicle) select.push("odometer");
	extraSelect.forEach((f) => { if (!select.includes(f)) select.push(f); });

	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params: {
			select,
			groupBy: ["batteryID"],
			metaInfo: ["deviceID", "StateOfHealthBucket", "licensePlateNo"],
			filters: JSON.stringify(additionalFilters),
			skipPagination: true,
		},
		paramsSerializer: sohListParamsSerializer,
	};

	return GetData(config);
}

async function getBatteriesHealth(
	filters,
	sort = {},
	sohLevel = {},
	signal = undefined
) {
	let newRangeStart = sohLevel?.min || 0;
	let newRangeEnd = sohLevel?.max || 120;
	let apiFilters = { ...filters }
	apiFilters['rangeStart'] = newRangeStart
	apiFilters['rangeEnd'] = newRangeEnd
	var rawJson = {
		filters: JSON.stringify(apiFilters),
		sort
	};

	var config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health",
		headers: {
			"Content-Type": "application/json",
		},
		params: rawJson,
	};

	if (signal) {
		config.signal = signal
	}

	let data = GetData(config);
	return data;
}

async function getBatteryHealthDistribution(
	filters
) {
	var rawJson = {
		filters: JSON.stringify(filters),
	};

	var config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health_group",
		headers: {
			"Content-Type": "application/json",
		},
		params: rawJson,
	};

	let data = GetData(config);
	return data;
}

async function getBatteryHealthSearchSuggestions(columnName, search, limit) {
	var rawJson = {
		columnName: columnName,
		columnSearch: search,
		columnLimit: limit,
	};

	var config = {
		method: "get",
		url: "/api/health/dashboard/search",
		headers: {
			"Content-Type": "application/json",
		},
		params: rawJson,
	};

	let data = GetData(config);
	return data;
}


async function getBatteryHealthKpis(batteryID) {
	var rawJson = {};
	var config = {
		method: "get",
		url: `/api/health/digital_twin/${batteryID}/kpis`,
		headers: {
			"Content-Type": "application/json",
		},
		params: rawJson,
	};

	let data = GetData(config);
	return data;
}

async function getFleetBatteryHealthV2Kpis(globalFilters = {}) {
	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/kpis",
		headers: buildSohListHeaders(globalFilters),
		params: {
			criticalSOHThreshold: 70,
			abnormalSOHDropThreshold: 1.5,
		},
	};

	return GetData(config);
}

async function getRULBreakdownDrillDownData(
	globalFilters = {},
	rulBucketKey = "RULMonthsBucket",
	rulBucketValue = "",
	select = [],
	metaInfo = [],
	page = 1,
	pageSize = 10,
	sortBy = [],
	additionalFilters = []
) {
	const filters = [
		...(rulBucketValue ? [{ name: rulBucketKey, value: [rulBucketValue] }] : []),
		...additionalFilters,
	];

	const params = {
		select,
		groupBy: ["batteryID"],
		metaInfo,
		filters: JSON.stringify(filters),
		skipPagination: false,
		page,
		pageSize,
	};

	if (sortBy?.length) {
		params.sortBy  = sortBy[0].column;
		params.orderBy = sortBy[0].direction;
	}

	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params,
		paramsSerializer: sohListParamsSerializer,
	};

	return GetData(config);
}

// Paginated battery-list params shared by the SOH drill-downs (v2/list and v2/history/list).
const buildSohDrillDownListParams = (select, metaInfo, filters, page, pageSize, sortBy) => {
	const params = {
		select,
		groupBy: ["batteryID"],
		metaInfo,
		filters: JSON.stringify(filters),
		skipPagination: false,
		page,
		pageSize,
	};

	if (sortBy?.length) {
		params.sortBy = sortBy[0].column;
		params.orderBy = sortBy[0].direction;
	}
	return params;
};

const buildSohBucketFilters = (sohBucket, additionalFilters = []) => [
	...(sohBucket ? [{ name: "StateOfHealthBucket", value: [sohBucket] }] : []),
	...additionalFilters,
];

async function getSOHBreakdownDrillDownData(
	globalFilters = {},
	sohBucket = "",
	select = [],
	metaInfo = [],
	page = 1,
	pageSize = 10,
	sortBy = [],
	additionalFilters = []
) {
	const filters = buildSohBucketFilters(sohBucket, additionalFilters);
	const params = buildSohDrillDownListParams(select, metaInfo, filters, page, pageSize, sortBy);

	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params,
		paramsSerializer: sohListParamsSerializer,
	};

	return GetData(config);
}

const SOH_HISTORY_LIST_URL = "/api/health/dashboard/fleet_battery_health/v2/history/list";

/**
 * Monthly SOH bucket counts plus where each bucket's batteries are the next month
 * (SOH bucket migration sankey). The window is whole months, at most 12 inclusive.
 * response.data: [{ xLabel: "YYYY-MM", metricsArray: [{ type: <bucket>, value, nextMonth: [{ type, value }] }] }]
 */
/**
 * The history endpoint's `sohBuckets` param: the chart's own SOH bucket list
 * ([{ label, range: { min, max } }]), which the backend groups and filters by instead
 * of org meta stateOfHealthBuckets. Nothing is sent for a missing or empty list.
 */
function withSohBucketsParam(params, sohBuckets) {
	if (Array.isArray(sohBuckets) && sohBuckets.length > 0) {
		params.sohBuckets = JSON.stringify(sohBuckets);
	}
	return params;
}

async function getSOHHistoryChartData(globalFilters = {}, { startDate, endDate, entityType, sohBuckets } = {}, additionalFilters = []) {
	const config = {
		method: "get",
		url: SOH_HISTORY_LIST_URL,
		headers: buildSohListHeaders(globalFilters),
		params: withSohBucketsParam({
			startDate,
			endDate,
			// Unit level counted ("battery" | "pack" | "string"); the backend defaults to battery.
			entityType,
			granularity: "MONTHLY",
			select: ["DistinctVehicleCount"],
			groupBy: ["StateOfHealthBucket"],
			includeNextMonth: true,
			skipPagination: true,
			filters: JSON.stringify(additionalFilters),
		}, sohBuckets),
		paramsSerializer: sohListParamsSerializer,
	};
	return GetData(config);
}

/**
 * Unit list for one SOH bucket in one month, or one bucket transition across two
 * months (list mode, no granularity). SOH fields in the response refer to the (last)
 * month. Same arguments as getSOHBreakdownDrillDownData plus { startDate, endDate,
 * entityType, sohBuckets }; with entityType the rows are units of that level (groupBy
 * entityID); sohBuckets must be the list the chart was drawn with, so bucket labels in
 * the filters resolve.
 * Only the fields in utils/sohHistoryFields.js are accepted (others return 400).
 */
async function getSOHHistoryDrillDownData(
	globalFilters = {},
	sohBucket = "",
	select = [],
	metaInfo = [],
	page = 1,
	pageSize = 10,
	sortBy = [],
	additionalFilters = [],
	{ startDate, endDate, entityType, sohBuckets } = {}
) {
	const filters = buildSohBucketFilters(sohBucket, additionalFilters);
	const params = withSohBucketsParam({
		startDate,
		endDate,
		...buildSohDrillDownListParams(select, metaInfo, filters, page, pageSize, sortBy),
	}, sohBuckets);
	// With a unit level: one row per unit of that level (xLabel = unit id).
	if (entityType) {
		params.entityType = entityType;
		params.groupBy = ["entityID"];
	}

	const config = {
		method: "get",
		url: SOH_HISTORY_LIST_URL,
		headers: buildSohListHeaders(globalFilters),
		params,
		paramsSerializer: sohListParamsSerializer,
	};
	return GetData(config);
}

async function getSOHScatterDetailsTableData(
	globalFilters = {},
	select = [],
	metaInfo = [],
	additionalFilters = [],
	page = 1,
	pageSize = 10,
	sortBy = []
) {
	const params = {
		select,
		groupBy: ["batteryID"],
		metaInfo,
		filters: JSON.stringify(additionalFilters),
		skipPagination: false,
		page,
		pageSize,
	};

	if (sortBy?.length) {
		params.sortBy  = sortBy[0].column;
		params.orderBy = sortBy[0].direction;
	}

	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params,
		paramsSerializer: sohListParamsSerializer,
	};

	return GetData(config);
}

async function getSOHDetailsTableAction(globalFilters = {}, body = {}) {
	const config = {
		method: "post",
		url: "/api/health/dashboard/fleet_battery_health/v2/list-actions",
		headers: buildSohListHeaders(globalFilters),
		data: body,
	};
	return GetData(config);
}

async function getRULDistribution(globalFilters = {}, rulBucketKey = "RULMonthsBucket", additionalFilters = []) {
	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params: {
			select: ["DistinctVehicleCount"],
			groupBy: [rulBucketKey],
			filters: JSON.stringify(additionalFilters),
			skipPagination: true,
		},
		paramsSerializer: sohListParamsSerializer,
	};
	return GetData(config);
}

const RUL_UNIT_METRIC = {
	months: "RULCalendarAgeDaysPredicted",
	cycles: "RULCyclesPredicted",
	km:     "RULKmPredicted",
};

const RUL_ALL_METRICS = ["RULCalendarAgeDaysPredicted", "RULCyclesPredicted", "RULKmPredicted"];

// Only months / cycles: the backend km bucket is constant-driven with labels that do not
// match the frontend km buckets, so km is always classified client-side.
const RUL_UNIT_BUCKET_KEY = {
	months: "RULMonthsBucket",
	cycles: "RULCyclesBucket",
};

async function getRULScatterData(globalFilters = {}, unit = "months", additionalFilters = [], supportedUnits = []) {
	// Fetch metrics for all supported units so the tooltip can show all of them.
	// Fall back to all metrics when supportedUnits is not configured.
	const select = supportedUnits.length > 0
		? supportedUnits.map((u) => RUL_UNIT_METRIC[u]).filter(Boolean)
		: RUL_ALL_METRICS;
	// The active unit's bucket label (incl. "RUL Unavailable") comes from the server so
	// the chart uses the same boundaries as the donut and filters. The backend returns the
	// field as null for every row when the org has no bucket meta for that unit; callers
	// decide via getRULServerBucketUnits whether to trust it or classify locally.
	const metaInfo = ["deviceID", "manufacturingDate"];
	if (RUL_UNIT_BUCKET_KEY[unit]) metaInfo.push(RUL_UNIT_BUCKET_KEY[unit]);
	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers: buildSohListHeaders(globalFilters),
		params: {
			select,
			groupBy:         ["batteryID"],
			metaInfo,
			filters:         JSON.stringify(additionalFilters),
			skipPagination:  true,
		},
		paramsSerializer: sohListParamsSerializer,
	};
	return GetData(config);
}

async function getWarrantyConfigs(globalFilters = {}, configName = null) {
	const params = {};
	if (configName) params.configName = configName;

	const config = {
		method: "get",
		url: "/api/v1/warranty/configs",
		headers: buildSohListHeaders(globalFilters),
		params,
	};
	return GetData(config);
}

async function getWarrantyBucketCounts(configName, globalFilters = {}) {
	const headers = buildSohListHeaders(globalFilters);

	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers,
		params: {
			groupBy: ["WarrantyBucket"],
			select: ["DistinctVehicleCount"],
			filters: JSON.stringify([{ name: "configName", value: [configName] }]),
			skipPagination: true,
		},
		paramsSerializer: sohListParamsSerializer,
	};
	return GetData(config);
}

async function getWarrantyDrillDownData(
	configName,
	warrantyBucket,
	page = 1,
	pageSize = 20,
	sortBy = [],
	globalFilters = {},
	select = ["RULCyclesPredicted", "RULKmPredicted", "warrantyRemainingMonths", "warrantyRemainingCycles", "warrantyRemainingKm", "warrantyRemainingDays"],
	metaInfo = ["licensePlateNo", "pingStatus"]
) {
	const headers = buildSohListHeaders(globalFilters);

	const filters = [
		{ name: "configName", value: [configName] },
		{ name: "WarrantyBucket", value: [warrantyBucket] },
	];

	const params = {
		groupBy: ["batteryID"],
		metaInfo,
		select,
		filters: JSON.stringify(filters),
		page,
		pageSize,
	};

	if (sortBy?.length) {
		params.sortBy = sortBy[0].column;
		params.orderBy = sortBy[0].direction;
	}

	const config = {
		method: "get",
		url: "/api/health/dashboard/fleet_battery_health/v2/list",
		headers,
		params,
		paramsSerializer: sohListParamsSerializer,
	};
	return GetData(config);
}

export {
	getBatteriesHealth,
	getBatteryHealthDistribution,
	getBatteryHealthSearchSuggestions,
	getBatteryHealthKpis,
	getFleetBatteryHealthV2Kpis,
	getSOHDistribution,
	getSOHScatterData,
	getRULScatterData,
	getRULDistribution,
	getSOHBreakdownChartData,
	getSOHBreakdownGroupByData,
	getSOHBreakdownDrillDownData,
	getSOHHistoryChartData,
	getSOHHistoryDrillDownData,
	getRULBreakdownChartData,
	getRULBreakdownDrillDownData,
	getSOHScatterDetailsTableData,
	getSOHDetailsTableAction,
	getWarrantyConfigs,
	getWarrantyBucketCounts,
	getWarrantyDrillDownData,
};
